/**
 * Matches [xxx to xxx]
 */
export const rangeQueryPattern = '\\[(.+?) +[Tt][Oo] +(.+?)\\]';
export const rangeQueryPatternRegex = new RegExp(rangeQueryPattern);

/**
 * Matches relative time patterns like +30d, -7h, +1w, -5m, +2M, +1Y
 * Supports: m (minutes), h (hours), d (days), w (weeks), M (months), Y (years)
 */
export const relativeTimePattern = '^([+-])(\\d+)([mhdwMY])$';
export const relativeTimePatternRegex = new RegExp(relativeTimePattern);

/**
 * Detects relative time patterns within a string (for checking if string contains relative syntax)
 * Avoids matching parts of ISO dates with negative lookahead
 */
export const relativeTimeDetectPattern = '[+-]\\d+[mhdwMY](?![:\\d-])';
export const relativeTimeDetectPatternRegex = new RegExp(relativeTimeDetectPattern);

/**
 * Strict date range pattern with exact " to " separator
 * Used for parsing date range syntax like "[2024-01-01 to 2024-12-31]"
 */
export const dateRangePattern = '^\\[(.*) to (.*)\\]$';
export const dateRangePatternRegex = new RegExp(dateRangePattern);


export enum CognitiveServiceProvider {
  AWS = 'AWS',
  GOOGLE = 'Google',
  IBM = 'IBM',
  MICROSOFT = 'Microsoft',
  TEXTRAZOR = 'Textrazor',                // no longer used
  ZOOM = 'Zoom',
  SPEECHMATICS = 'Speechmatics',
  TRINT = 'Trint',
  AMBERSCRIPT = 'Amberscript',
  RECAPP = 'Recapp',                      // no longer used
  RECAPP2 = 'Recapp2',
  REMOVE_BG = 'RemoveBg',                 // no longer used
  MXCODER = 'Mxcoder',
  EXPEDITED = 'Expedited',
  BULK = 'Bulk',
  STANDARD = 'Standard',
  EDIT_PROXY_CLOUD = 'editProxyCloud',
  EDIT_PROXY_ON_PREMISE = 'editProxyOnPrem',
  RENDER_SEQUENCE_CLOUD = 'renderSequenceCloud',
  RENDER_SEQUENCE_ON_PREMISE = 'renderSequenceOnPremise',
  P2_INGEST = 'p2Ingest',
  SRT_IMPORT = 'srtImport',
  CLOUD_LIVE = 'cloudLive',
  KELDA_LIVE_FOLDER = 'keldaLiveFolder',
  KELDA_SEGMENTED_PROXY_GEN = 'keldaSegmentedProxyGen',
  CONSUMPTION_BASED_MXCODER = 'consumptionBasedMXCoder',
  CONSUMPTION_BASED_EDIT_PROXY_CLOUD = 'consumptionBasedEditProxyCloud',
  SCENE_DETECTION = 'Scene Detection',
  MIMIR = 'Mimir',
  KELDA_TRANSCODING = 'keldaTranscoding', // For video
  KELDA_TRANSCODING_AUDIO = 'keldaTranscodingAudio',
}

export const KELDA_PROVIDERS = [
  CognitiveServiceProvider.KELDA_TRANSCODING,
  CognitiveServiceProvider.KELDA_TRANSCODING_AUDIO,
  CognitiveServiceProvider.EDIT_PROXY_ON_PREMISE,
  CognitiveServiceProvider.KELDA_SEGMENTED_PROXY_GEN,
  CognitiveServiceProvider.KELDA_LIVE_FOLDER,
  CognitiveServiceProvider.P2_INGEST,
] as const;

export const CLOUD_PROVIDERS = [
  CognitiveServiceProvider.MXCODER,
  CognitiveServiceProvider.CONSUMPTION_BASED_MXCODER,
  CognitiveServiceProvider.EDIT_PROXY_CLOUD,
  CognitiveServiceProvider.CONSUMPTION_BASED_EDIT_PROXY_CLOUD,
  CognitiveServiceProvider.CLOUD_LIVE,
  CognitiveServiceProvider.AWS,
] as const;

export const RENDERING_PROVIDERS = [
  CognitiveServiceProvider.RENDER_SEQUENCE_ON_PREMISE,
  CognitiveServiceProvider.RENDER_SEQUENCE_CLOUD,
] as const;

export const MIMIR_CLAIMS_NAMESPACE = 'mimir';
export const MIMIR_TENANT_ID_CLAIM = MIMIR_CLAIMS_NAMESPACE + ':tenant_id';
export const MIMIR_GROUPS_CLAIM = MIMIR_CLAIMS_NAMESPACE + ':groups';

export enum TranscodingServiceProvider {
  MIMIR_MXCODER_PROXYGEN = 'mimirMxcoderProxygen',
}


/**
 * Maximum image size in bytes (100MB) for EXIF embedding.
 * Images larger than this size will not have EXIF data embedded
 * while downloading or sending to storage to optimize performance.
 */
export const MAX_IMAGE_SIZE_FOR_EXIF_EMBEDDING_BYTES = 100 * 1024 * 1024;

/**
 * Ingest S3 constants
 */
// Wait 7 days for purge unless a specific purge date has been set
export const PURGE_DEFAULT_DELAY = 7;

/**
 * Premiere constant to define a local transfer request that is not to
 * be posted to server for processing
 */
export const PREMIERE_STANDALONE_STORAGE_ID = 'premiereStandaloneStorage';

export const defaultLambdaInvocationParams = {
  maxRetries: 5,
  httpOptions: {
    // todo: 15 mins as default timeout is too high, and without any effect as the
    // calling lambda would always timeout before. Review usages and reduce.
    timeout: 900000,
    connectTimeout: 3000,
  },
};

/**
 * Kelda constants
 */
// When a Kelda request has been in processing state for more than a given delay without
// being updated, we assume that the node processing it has died and we consider
// the request up for grab again. The kelda client must therefore make sure that
// the time between updates is lower than this value.
export const KELDA_REQUEST_INACTIVITY_THRESHOLD_IN_MS = 60 * 1000;

/**
 * Cold storage constants for Lifecycle configuration and archive tags
 */
export const GLACIER_LIFECYCLE_RULE_ID = 'Glacier-LifeCycleConfiguration';
export const DEEP_ARCHIVE_LIFECYCLE_RULE_ID = 'DeepArchive-LifeCycleConfiguration';
export const GLACIER_TAG_KEY = 'archiveToGlacier';
export const DEEP_ARCHIVE_TAG_KEY = 'archiveToDeepArchive';
export const COLD_STORAGE_VERIFICATION_FILE_NAME = '.mimir-cold-storage-info';
/**
 * Lifecycle rule ID for EXIF‐embedded images (delete after 1 day).
 */
export const MDF_TO_EXIF_EMBEDDED_IMAGE_LIFECYCLE_RULE_ID = 'exifEmbeddedImageShouldBeDeletedAfter1Day';
export const MDF_TO_EXIF_EMBEDDED_IMAGE_TAG_KEY = 'MDFEmbeddedImage';
export const MDF_TO_EXIF_EMBEDDED_IMAGE_TAG_VALUE = '1';
export const MDF_TO_EXIF_EMBEDDED_IMAGE_PATH_PREFIX = 'mdfToEXIFMappings/';

/**
 * You should probably not be using this constant directly, because the
 * semantics of this header has changed over time. It used to always contain a
 * JWT token obtained from the Cognito User Pool of the auth stack of the Mimir
 * system.
 *
 * However, now we no longer want any code to make this assumption, since we'd
 * like to transmit other kinds of secret access tokens as well. This includes
 * API keys and customer provided access tokens that we verify towards their
 * authorization servers.
 *
 * The name 'x-mimir-cognito-id-token' doesn't fit anymore, since the token is
 * not always from Cognito, but due to the way that authorizer cache key
 * configuration works, we cannot support more than one header at the API
 * gateway layer, and we need to continue supporting the existing header name
 * for backwards compatibility. This means we're stuck with the name until we
 * start rewriting headers at the CloudFront layer.
 */
export const MIMIR_JWT_HTTP_HEADER = 'x-mimir-cognito-id-token';
